// ===== BEGIN FILE: src/components/chat/ChatMarkdown.tsx =====
/**
 * ChatMarkdown — Renders AI response markdown with MUI Joy styling.
 * Uses react-markdown + remark-gfm for parsing.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Typography, Box } from '@mui/joy';

interface ChatMarkdownProps {
  content: string;
  isStreaming?: boolean;
}

export function ChatMarkdown({ content, isStreaming }: ChatMarkdownProps) {
  return (
    <Box
      sx={{
        '& p': {
          margin: 0,
          marginBottom: '0.55em',
          lineHeight: 1.7,
          fontSize: '0.875rem',
          fontWeight: 400,
          letterSpacing: '0.008em',
          '&:last-child': { marginBottom: 0 }
        },
        '& strong': { fontWeight: 600, color: 'inherit' },
        '& em': { fontStyle: 'italic', opacity: 0.85 },
        '& h3': {
          fontWeight: 700,
          fontSize: '0.825rem',
          color: 'text.primary',
          marginTop: '1em',
          marginBottom: '0.35em',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        },
        '& h4': {
          fontWeight: 600,
          fontSize: '0.825rem',
          color: 'text.secondary',
          marginTop: '0.8em',
          marginBottom: '0.3em',
          letterSpacing: '0.02em',
        },
        '& ul, & ol': {
          margin: '0.35em 0',
          paddingLeft: '1.35em'
        },
        '& li': {
          marginBottom: '0.3em',
          lineHeight: 1.65,
          fontSize: '0.875rem',
          letterSpacing: '0.008em',
        },
        '& hr': {
          border: 'none',
          borderTop: '1px solid',
          borderColor: 'divider',
          margin: '0.85em 0'
        },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'neutral.300',
          paddingLeft: '0.875em',
          margin: '0.6em 0',
          color: 'text.secondary',
          fontStyle: 'normal',
          fontSize: '0.85rem',
          opacity: 0.85,
        },
        '& table': {
          fontSize: '0.8rem',
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '0.75em',
        },
        '& th': {
          fontWeight: 600,
          fontSize: '0.72rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'text.secondary',
          padding: '5px 10px',
          borderBottom: '2px solid',
          borderColor: 'divider',
          textAlign: 'left',
        },
        '& td': {
          fontSize: '0.8rem',
          padding: '5px 10px',
          borderBottom: '1px solid',
          borderColor: 'divider',
          lineHeight: 1.55,
          color: 'text.primary',
        },
        wordBreak: 'break-word',
        '@media (max-width: 600px)': {
          '& p': { fontSize: '0.9375rem', lineHeight: 1.8 },
          '& li': { fontSize: '0.9375rem', lineHeight: 1.8 },
          '& h3': { fontSize: '0.9375rem' },
          '& h4': { fontSize: '0.9375rem' },
          '& blockquote': { fontSize: '0.9rem' },
          '& td': { fontSize: '0.875rem' },
          '& th': { fontSize: '0.78rem' },
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Override renderer to use neutral Typography-like spans
          p: ({ children }) => (
            <p style={{ margin: 0, marginBottom: '0.6em', lineHeight: 1.65 }}>{children}</p>
          ),
          strong: ({ children }) => (
            <strong style={{ fontWeight: 700 }}>{children}</strong>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
      {isStreaming && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: 7,
            height: 14,
            bgcolor: 'success.500',
            ml: 0.5,
            verticalAlign: 'text-bottom',
            animation: 'chatCursorBlink 0.9s steps(1) infinite',
            '@keyframes chatCursorBlink': {
              '0%, 49%': { opacity: 1 },
              '50%, 100%': { opacity: 0 }
            }
          }}
        />
      )}
    </Box>
  );
}

export default ChatMarkdown;
// ===== END FILE: src/components/chat/ChatMarkdown.tsx =====
